import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import {
    buildAttachmentKey,
    uploadAttachmentToS3,
    pickRecorderMimeType,
    isAlreadyM4a,
    transcodeToM4a
} from '../utils/chatAttachments';
import './SupportChat.css';

const MAX_IMAGE_BYTES = 8 * 1024 * 1024; // sem teto imposto pela API — cap do lado admin

// A URL de leitura expira rápido (~60s) — nunca cacheamos entre renders,
// cada componente busca a sua na hora que precisa ser exibida/tocada.
const fetchAttachmentUrl = (msg, bucketName) => apiService.getReadFileUrl({
    fileName: msg.metadata?.url,
    bucketName,
    fileType: msg.metadata?.mimeType
});

const formatDuration = (durationMs) => {
    if (!durationMs && durationMs !== 0) return '';
    const totalSeconds = Math.round(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const SupportImageAttachment = ({ msg, bucketName }) => {
    const [state, setState] = useState({ url: null, loading: true, error: false });

    const load = async () => {
        setState({ url: null, loading: true, error: false });
        const result = await fetchAttachmentUrl(msg, bucketName);
        if (result.success && result.data?.downloadUrl) {
            setState({ url: result.data.downloadUrl, loading: false, error: false });
        } else {
            setState({ url: null, loading: false, error: true });
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [msg.id]);

    if (state.loading) {
        return <div className="support-attachment-loading">Carregando imagem...</div>;
    }

    if (state.error || !state.url) {
        return (
            <div className="support-attachment-error">
                <span>Não foi possível carregar a imagem.</span>
                <button type="button" className="support-attachment-reload-btn" onClick={load}>Tentar novamente</button>
            </div>
        );
    }

    return (
        <a href={state.url} target="_blank" rel="noopener noreferrer" className="support-attachment-image-link">
            <img
                src={state.url}
                alt="Anexo enviado no chat"
                className="support-attachment-image"
                onError={() => setState(prev => ({ ...prev, error: true }))}
            />
        </a>
    );
};

const SupportAudioAttachment = ({ msg, bucketName }) => {
    const [state, setState] = useState({ url: null, loading: false, error: false });

    const load = async () => {
        setState({ url: null, loading: true, error: false });
        const result = await fetchAttachmentUrl(msg, bucketName);
        if (result.success && result.data?.downloadUrl) {
            setState({ url: result.data.downloadUrl, loading: false, error: false });
        } else {
            setState({ url: null, loading: false, error: true });
        }
    };

    const duration = formatDuration(msg.metadata?.durationMs);

    if (state.loading) {
        return <div className="support-attachment-loading">Carregando áudio...</div>;
    }

    if (state.error) {
        return (
            <div className="support-attachment-error">
                <span>Não foi possível carregar o áudio.</span>
                <button type="button" className="support-attachment-reload-btn" onClick={load}>Tentar novamente</button>
            </div>
        );
    }

    if (state.url) {
        return (
            <audio
                controls
                autoPlay
                src={state.url}
                className="support-attachment-audio"
                onError={() => setState({ url: null, loading: false, error: true })}
            >
                Seu navegador não suporta áudio.
            </audio>
        );
    }

    return (
        <button type="button" className="support-attachment-play-btn" onClick={load}>
            ▶ Ouvir áudio{duration ? ` (${duration})` : ''}
        </button>
    );
};

const SupportChat = () => {
    const [searchParams] = useSearchParams();
    const urlConversationId = searchParams.get('conversation_id');

    const [user, setUser] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Track which companies/stores/salesmen/conversations are expanded
    const [expandedCompanies, setExpandedCompanies] = useState({});
    const [expandedStores, setExpandedStores] = useState({});
    const [expandedSalesmen, setExpandedSalesmen] = useState({});
    const [expandedConversations, setExpandedConversations] = useState({});

    // Messages per store (keyed by `subdomain-store_id`)
    const [storeMessages, setStoreMessages] = useState({});
    const [loadingMessages, setLoadingMessages] = useState({});

    // Reply inputs per conversation
    const [replyTexts, setReplyTexts] = useState({});
    const [sendingReply, setSendingReply] = useState({});

    const messagesEndRefs = useRef({});
    const targetConvRef = useRef(null);
    const loadedRef = useRef(false);

    // IDs de mensagens já marcadas como lidas nesta sessão — evita reenviar
    // /support-message/read toda vez que a conversa é reaberta/atualizada.
    const markedReadRef = useRef(new Set());

    // Marca como lidas (do ponto de vista do admin) as mensagens do vendedor
    // que já apareceram na tela desta conversa. Nunca marca mensagens enviadas
    // pelo próprio admin (o backend também não deixaria).
    const markConversationAsSeen = (conv, storeId, adminUserId) => {
        if (!adminUserId || !storeId || !conv) return;
        const idsToMark = (conv.messages || [])
            .filter(m => m.sender_role !== 'admin' && typeof m.id === 'number' && !markedReadRef.current.has(m.id))
            .map(m => m.id);
        if (!idsToMark.length) return;

        idsToMark.forEach(id => markedReadRef.current.add(id));
        apiService.markSupportMessagesRead({ user_id: adminUserId, store_id: storeId, ids: idsToMark })
            .then(result => {
                if (!result.success) {
                    idsToMark.forEach(id => markedReadRef.current.delete(id));
                }
            })
            .catch(() => {
                idsToMark.forEach(id => markedReadRef.current.delete(id));
            });
    };

    useEffect(() => {
        if (loadedRef.current) return;
        loadedRef.current = true;
        loadInitialData();

        async function loadInitialData() {
            try {
                setLoading(true);
                const [userData, storesData] = await Promise.all([
                    apiService.getUserInfo(),
                    apiService.listStoresByUser()
                ]);
                setUser(userData.user);
                const loadedCompanies = storesData.companies || [];
                setCompanies(loadedCompanies);

                // Carregar mensagens de todas as lojas em paralelo
                const allStoreKeys = [];
                const allStoreRequests = [];
                loadedCompanies.forEach((company, companyIdx) => {
                    const subdomain = company.subdomain || '';
                    const stores = (company.units || []).flatMap(unit => unit.stores || []);
                    stores.forEach(store => {
                        const storeKey = `${subdomain}-${store.store_id}`;
                        allStoreKeys.push({ storeKey, companyIdx });
                        allStoreRequests.push(
                            apiService.getSupportMessages({ store_id: store.store_id, subdomain })
                                .then(result => ({ storeKey, result }))
                                .catch(() => ({ storeKey, result: { success: false } }))
                        );
                    });
                });

                const results = await Promise.all(allStoreRequests);

                // Processar resultados e determinar quais empresas expandir
                const newStoreMessages = {};
                const companiesWithChats = new Set();
                results.forEach(({ storeKey, result }) => {
                    if (result.success && result.data) {
                        newStoreMessages[storeKey] = result.data;
                        const hasMessages = (result.data.messages || []).length > 0;
                        if (hasMessages) {
                            const match = allStoreKeys.find(s => s.storeKey === storeKey);
                            if (match) companiesWithChats.add(match.companyIdx);
                        }
                    } else {
                        newStoreMessages[storeKey] = { messages: [], total: 0 };
                    }
                });

                setStoreMessages(newStoreMessages);

                // Auto-expandir empresas que possuem chats e expandir tudo que está pendente
                const autoExpanded = {};
                const autoExpandedStores = {};
                const autoExpandedSalesmen = {};
                const autoExpandedConversations = {};

                companiesWithChats.forEach(idx => {
                    autoExpanded[idx] = true;
                    const company = loadedCompanies[idx];
                    if (!company) return;
                    const sub = company.subdomain || '';
                    const companyStores = (company.units || []).flatMap(unit => unit.stores || []);
                    companyStores.forEach(store => {
                        const sk = `${sub}-${store.store_id}`;
                        const md = newStoreMessages[sk];
                        if (!md || !(md.messages || []).length) return;
                        const groups = groupBySalesmanAndConversation(md.messages, userData.user?.user_id);
                        const hasPending = groups.some(s => s.conversations.some(c => {
                            const last = c.messages[c.messages.length - 1];
                            return last && last.sender_role !== 'admin';
                        }));
                        if (hasPending) {
                            autoExpandedStores[sk] = true;
                            groups.forEach(salesman => {
                                const sPending = salesman.conversations.some(c => {
                                    const last = c.messages[c.messages.length - 1];
                                    return last && last.sender_role !== 'admin';
                                });
                                if (sPending) {
                                    autoExpandedSalesmen[`${sk}-user-${salesman.userId}`] = true;
                                    salesman.conversations.forEach(conv => {
                                        const last = conv.messages[conv.messages.length - 1];
                                        if (last && last.sender_role !== 'admin') {
                                            autoExpandedConversations[`${sk}-${conv.conversation_id}`] = true;
                                            markConversationAsSeen(conv, store.store_id, userData.user?.user_id);
                                        }
                                    });
                                }
                            });
                        }
                    });
                });

                setExpandedCompanies(autoExpanded);
                setExpandedStores(autoExpandedStores);
                setExpandedSalesmen(autoExpandedSalesmen);
                setExpandedConversations(autoExpandedConversations);

                // Se conversation_id foi passado na URL, expandir tudo até chegar nela
                if (urlConversationId) {
                    loadedCompanies.forEach((company, compIdx) => {
                        const sub = company.subdomain || '';
                        const compStores = (company.units || []).flatMap(unit => unit.stores || []);
                        compStores.forEach(store => {
                            const sk = `${sub}-${store.store_id}`;
                            const md = newStoreMessages[sk];
                            if (!md || !(md.messages || []).length) return;
                            const groups = groupBySalesmanAndConversation(md.messages, userData.user?.user_id);
                            groups.forEach(salesman => {
                                salesman.conversations.forEach(conv => {
                                    if (String(conv.conversation_id) === String(urlConversationId)) {
                                        autoExpanded[compIdx] = true;
                                        autoExpandedStores[sk] = true;
                                        autoExpandedSalesmen[`${sk}-user-${salesman.userId}`] = true;
                                        autoExpandedConversations[`${sk}-${conv.conversation_id}`] = true;
                                        markConversationAsSeen(conv, store.store_id, userData.user?.user_id);
                                    }
                                });
                            });
                        });
                    });
                    setExpandedCompanies({ ...autoExpanded });
                    setExpandedStores({ ...autoExpandedStores });
                    setExpandedSalesmen({ ...autoExpandedSalesmen });
                    setExpandedConversations({ ...autoExpandedConversations });
                }
            } catch (err) {
                console.error('Erro ao carregar dados:', err);
                setError('Erro ao carregar dados. Tente novamente.');
            } finally {
                setLoading(false);
            }
        }
    }, [urlConversationId]);

    // Scroll para a conversa alvo quando aberta via URL
    useEffect(() => {
        if (!loading && urlConversationId && targetConvRef.current) {
            setTimeout(() => {
                targetConvRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
        }
    }, [loading, urlConversationId]);

    const toggleCompany = (companyIdx) => {
        const isCurrentlyOpen = expandedCompanies[companyIdx];

        if (isCurrentlyOpen) {
            // Fechar empresa
            setExpandedCompanies(prev => ({ ...prev, [companyIdx]: false }));
        } else {
            // Abrir empresa e auto-expandir tudo que está pendente
            setExpandedCompanies(prev => ({ ...prev, [companyIdx]: true }));
            expandPendingItems(companyIdx);
        }
    };

    const expandPendingItems = (companyIdx) => {
        const company = companies[companyIdx];
        if (!company) return;
        const subdomain = company.subdomain || '';
        const stores = (company.units || []).flatMap(unit => unit.stores || []);
        const newExpandedStores = {};
        const newExpandedSalesmen = {};
        const newExpandedConversations = {};

        stores.forEach(store => {
            const storeKey = `${subdomain}-${store.store_id}`;
            const messagesData = storeMessages[storeKey];
            if (!messagesData) return;
            const groups = groupBySalesmanAndConversation(messagesData.messages, user?.user_id);
            const hasPending = groups.some(s => s.conversations.some(c => isConversationPending(c)));
            if (hasPending) {
                newExpandedStores[storeKey] = true;
                groups.forEach(salesman => {
                    const salesmanHasPending = salesman.conversations.some(c => isConversationPending(c));
                    if (salesmanHasPending) {
                        const salesmanKey = `${storeKey}-user-${salesman.userId}`;
                        newExpandedSalesmen[salesmanKey] = true;
                        salesman.conversations.forEach(conv => {
                            if (isConversationPending(conv)) {
                                newExpandedConversations[`${storeKey}-${conv.conversation_id}`] = true;
                                markConversationAsSeen(conv, store.store_id, user?.user_id);
                            }
                        });
                    }
                });
            }
        });

        setExpandedStores(prev => ({ ...prev, ...newExpandedStores }));
        setExpandedSalesmen(prev => ({ ...prev, ...newExpandedSalesmen }));
        setExpandedConversations(prev => ({ ...prev, ...newExpandedConversations }));
    };

    const toggleStore = (storeKey, store, subdomain) => {
        const wasExpanded = expandedStores[storeKey];
        setExpandedStores(prev => ({ ...prev, [storeKey]: !prev[storeKey] }));

        // Load messages when expanding a store for the first time
        if (!wasExpanded && !storeMessages[storeKey]) {
            loadStoreMessages(storeKey, store.store_id, subdomain);
        }
    };

    const toggleSalesman = (salesmanKey) => {
        setExpandedSalesmen(prev => ({ ...prev, [salesmanKey]: !prev[salesmanKey] }));
    };

    const toggleConversation = (convKey, conv, storeId) => {
        const willOpen = !expandedConversations[convKey];
        setExpandedConversations(prev => ({ ...prev, [convKey]: !prev[convKey] }));
        if (willOpen && conv) {
            markConversationAsSeen(conv, storeId, user?.user_id);
        }
    };

    const loadStoreMessages = async (storeKey, storeId, subdomain) => {
        setLoadingMessages(prev => ({ ...prev, [storeKey]: true }));
        try {
            const result = await apiService.getSupportMessages({
                store_id: storeId,
                subdomain: subdomain
            });
            if (result.success) {
                setStoreMessages(prev => ({ ...prev, [storeKey]: result.data }));
            } else {
                console.error('Erro ao carregar mensagens:', result.message);
                setStoreMessages(prev => ({ ...prev, [storeKey]: { messages: [], total: 0 } }));
            }
        } catch (err) {
            console.error('Erro ao carregar mensagens:', err);
            setStoreMessages(prev => ({ ...prev, [storeKey]: { messages: [], total: 0 } }));
        } finally {
            setLoadingMessages(prev => ({ ...prev, [storeKey]: false }));
        }
    };

    const handleReplyChange = (conversationKey, text) => {
        setReplyTexts(prev => ({ ...prev, [conversationKey]: text }));
    };

    // Reflete no estado local uma mensagem que acabou de ser aceita pela API,
    // sem esperar o próximo refresh/reload da conversa.
    const appendOptimisticMessage = (storeKey, { conversationId, message, messageType, metadata }) => {
        if (!user) return;
        const optimisticMsg = {
            id: `temp-${Date.now()}`,
            conversation_id: conversationId,
            user_id_sender: user.user_id,
            sender_name: user.name || user.username,
            sender_role: 'admin',
            message,
            message_type: messageType,
            metadata: metadata || null,
            created: new Date().toISOString()
        };
        setStoreMessages(prev => {
            const current = prev[storeKey] || { messages: [], total: 0 };
            return {
                ...prev,
                [storeKey]: {
                    ...current,
                    messages: [...(current.messages || []), optimisticMsg],
                    total: (current.total || 0) + 1
                }
            };
        });
    };

    const handleSendReply = async (conversationId, storeId, subdomain, storeKey, conversationUserId) => {
        const conversationKey = `${storeKey}-${conversationId}`;
        const message = (replyTexts[conversationKey] || '').trim();
        if (!message || !user) return;

        setSendingReply(prev => ({ ...prev, [conversationKey]: true }));
        try {
            const result = await apiService.sendSupportMessage({
                user_id: conversationUserId,
                user_id_admin: user.user_id,
                store_id: storeId,
                message,
                message_type: 'text',
                conversation_id: conversationId,
                subdomain
            });
            if (result.success) {
                setReplyTexts(prev => ({ ...prev, [conversationKey]: '' }));
                appendOptimisticMessage(storeKey, { conversationId, message, messageType: 'text' });
            }
        } catch (err) {
            console.error('Erro ao enviar mensagem:', err);
        } finally {
            setSendingReply(prev => ({ ...prev, [conversationKey]: false }));
        }
    };

    const handleKeyDown = (e, conversationId, storeId, subdomain, storeKey, conversationUserId) => {
    };

    // ===== Anexos (imagem/áudio) enviados pelo admin =====

    // busy: null | 'recording' | 'converting' | 'uploading-image' | 'uploading-audio'
    const [attachmentBusy, setAttachmentBusy] = useState({});
    const [attachmentError, setAttachmentError] = useState({});
    const [recordingElapsed, setRecordingElapsed] = useState({});
    const imageInputRef = useRef(null);
    const pendingImageTargetRef = useRef(null);
    const recordersRef = useRef({});

    const setAttachBusy = (convKey, status) => setAttachmentBusy(prev => ({ ...prev, [convKey]: status }));
    const setAttachErr = (convKey, msg) => setAttachmentError(prev => ({ ...prev, [convKey]: msg }));

    // Para o mic ligado e os timers de qualquer gravação em andamento ao desmontar.
    useEffect(() => {
        const recorders = recordersRef.current;
        return () => {
            Object.values(recorders).forEach(rec => {
                try { rec.mediaRecorder.stop(); } catch (_) { /* já parado */ }
                try { rec.stream.getTracks().forEach(t => t.stop()); } catch (_) { /* já parado */ }
                clearInterval(rec.timerId);
            });
        };
    }, []);

    const handleAttachImageClick = (convKey, conv, store, company, subdomain, storeKey, conversationUserId) => {
        if (attachmentBusy[convKey]) return;
        pendingImageTargetRef.current = { convKey, conv, store, company, subdomain, storeKey, conversationUserId };
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
            imageInputRef.current.click();
        }
    };

    const handleImageFileSelected = async (e) => {
        const file = e.target.files && e.target.files[0];
        const target = pendingImageTargetRef.current;
        pendingImageTargetRef.current = null;
        if (!file || !target || !user) return;

        const { convKey, conv, store, company, subdomain, storeKey, conversationUserId } = target;

        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            setAttachErr(convKey, 'Só é possível anexar imagens JPEG ou PNG.');
            return;
        }
        if (file.size > MAX_IMAGE_BYTES) {
            setAttachErr(convKey, 'Imagem maior que 8MB — reduza o tamanho antes de anexar.');
            return;
        }

        setAttachErr(convKey, null);
        setAttachBusy(convKey, 'uploading-image');
        try {
            const ext = file.type === 'image/png' ? 'png' : 'jpg';
            const fileName = buildAttachmentKey({ storeName: store.store_name, conversationId: conv.conversation_id, kind: 'image', ext });
            const uploadResult = await uploadAttachmentToS3({ blob: file, fileName, bucketName: company.bucket_name, fileType: file.type });
            if (!uploadResult.success) {
                setAttachErr(convKey, uploadResult.message || 'Erro ao subir a imagem.');
                return;
            }

            const metadata = { kind: 'image', url: uploadResult.key, mimeType: file.type };
            const sendResult = await apiService.sendSupportMessage({
                user_id: conversationUserId,
                user_id_admin: user.user_id,
                store_id: store.store_id,
                message: '📷 Imagem',
                message_type: 'image',
                metadata,
                conversation_id: conv.conversation_id,
                subdomain
            });
            if (!sendResult.success) {
                setAttachErr(convKey, sendResult.message || 'Erro ao enviar a imagem.');
                return;
            }
            appendOptimisticMessage(storeKey, { conversationId: conv.conversation_id, message: '📷 Imagem', messageType: 'image', metadata });
        } catch (err) {
            console.error('Erro ao anexar imagem:', err);
            setAttachErr(convKey, 'Erro inesperado ao anexar imagem.');
        } finally {
            setAttachBusy(convKey, null);
        }
    };

    const handleStartRecording = async (convKey) => {
        if (attachmentBusy[convKey]) return;
        setAttachErr(convKey, null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = pickRecorderMimeType();
            const mediaRecorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            const chunks = [];
            mediaRecorder.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
            const startedAt = Date.now();
            const timerId = setInterval(() => {
                setRecordingElapsed(prev => ({ ...prev, [convKey]: Math.round((Date.now() - startedAt) / 1000) }));
            }, 500);
            recordersRef.current[convKey] = { mediaRecorder, chunks, stream, startedAt, timerId };
            mediaRecorder.start();
            setRecordingElapsed(prev => ({ ...prev, [convKey]: 0 }));
            setAttachBusy(convKey, 'recording');
        } catch (err) {
            console.error('Erro ao iniciar gravação:', err);
            setAttachErr(convKey, 'Não foi possível acessar o microfone.');
        }
    };

    const handleStopRecording = async (convKey, conv, store, company, subdomain, storeKey, conversationUserId) => {
        const rec = recordersRef.current[convKey];
        if (!rec || !user) return;
        clearInterval(rec.timerId);

        const durationMs = Date.now() - rec.startedAt;
        const stopped = new Promise(resolve => { rec.mediaRecorder.onstop = resolve; });
        rec.mediaRecorder.stop();
        await stopped;
        rec.stream.getTracks().forEach(t => t.stop());
        delete recordersRef.current[convKey];
        setRecordingElapsed(prev => ({ ...prev, [convKey]: 0 }));

        const recordedType = rec.mediaRecorder.mimeType || 'audio/webm';
        const rawBlob = new Blob(rec.chunks, { type: recordedType });

        if (!rawBlob.size) {
            setAttachBusy(convKey, null);
            setAttachErr(convKey, 'Gravação vazia — tente novamente.');
            return;
        }

        try {
            let finalBlob = rawBlob;
            if (!isAlreadyM4a(recordedType)) {
                setAttachBusy(convKey, 'converting');
                finalBlob = await transcodeToM4a(rawBlob);
            }

            setAttachBusy(convKey, 'uploading-audio');
            const fileName = buildAttachmentKey({ storeName: store.store_name, conversationId: conv.conversation_id, kind: 'audio', ext: 'm4a' });
            const uploadResult = await uploadAttachmentToS3({ blob: finalBlob, fileName, bucketName: company.bucket_name, fileType: 'audio/mp4' });
            if (!uploadResult.success) {
                setAttachErr(convKey, uploadResult.message || 'Erro ao subir o áudio.');
                return;
            }

            const metadata = { kind: 'audio', url: uploadResult.key, mimeType: 'audio/mp4', durationMs };
            const sendResult = await apiService.sendSupportMessage({
                user_id: conversationUserId,
                user_id_admin: user.user_id,
                store_id: store.store_id,
                message: '🎙️ Áudio',
                message_type: 'audio',
                metadata,
                conversation_id: conv.conversation_id,
                subdomain
            });
            if (!sendResult.success) {
                setAttachErr(convKey, sendResult.message || 'Erro ao enviar o áudio.');
                return;
            }
            appendOptimisticMessage(storeKey, { conversationId: conv.conversation_id, message: '🎙️ Áudio', messageType: 'audio', metadata });
        } catch (err) {
            console.error('Erro ao processar áudio gravado:', err);
            setAttachErr(convKey, 'Erro ao converter/enviar o áudio.');
        } finally {
            setAttachBusy(convKey, null);
        }
    };

    const [clearingConversation, setClearingConversation] = useState({});
    const [refreshingConversation, setRefreshingConversation] = useState({});
    const [confirmModal, setConfirmModal] = useState(null);

    const handleRefreshConversation = async (storeKey, storeId, subdomain) => {
        if (refreshingConversation[storeKey]) return;
        setRefreshingConversation(prev => ({ ...prev, [storeKey]: true }));
        try {
            const result = await apiService.getSupportMessages({ store_id: storeId, subdomain });
            if (result.success && result.data) {
                setStoreMessages(prev => ({ ...prev, [storeKey]: result.data }));
                // Conversas já abertas continuam abertas após o refresh: marcar como
                // lidas quaisquer mensagens novas do vendedor que chegaram nelas.
                const groups = groupBySalesmanAndConversation(result.data.messages, user?.user_id);
                groups.forEach(salesman => {
                    salesman.conversations.forEach(conv => {
                        const convKey = `${storeKey}-${conv.conversation_id}`;
                        if (expandedConversations[convKey]) {
                            markConversationAsSeen(conv, storeId, user?.user_id);
                        }
                    });
                });
            }
        } catch (err) {
            console.error('Erro ao atualizar conversa:', err);
        } finally {
            setRefreshingConversation(prev => ({ ...prev, [storeKey]: false }));
        }
    };

    const handleClearConversation = async (conv, storeId, storeKey) => {
        const convKey = `${storeKey}-${conv.conversation_id}`;

        // Mostrar modal de confirmação
        setConfirmModal({
            convKey,
            messageCount: conv.messages.length,
            onConfirm: async () => {
                setConfirmModal(null);
                setClearingConversation(prev => ({ ...prev, [convKey]: true }));
                try {
                    // Enviar deletes sequencialmente para evitar rate limiting
                    for (const msg of conv.messages) {
                        await apiService.deleteSupportMessage({
                            user_id: msg.user_id_sender,
                            store_id: storeId,
                            message_id: msg.id
                        });
                    }

                    // Remover mensagens localmente
                    setStoreMessages(prev => {
                        const current = prev[storeKey] || { messages: [], total: 0 };
                        const convMsgIds = new Set(conv.messages.map(m => m.id));
                        const remaining = (current.messages || []).filter(m => !convMsgIds.has(m.id));
                        return {
                            ...prev,
                            [storeKey]: { ...current, messages: remaining, total: remaining.length }
                        };
                    });
                } catch (err) {
                    console.error('Erro ao limpar conversa:', err);
                } finally {
                    setClearingConversation(prev => ({ ...prev, [convKey]: false }));
                }
            }
        });
    };

    const formatTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Group messages by salesman (non-admin user_id_sender), then by conversation_id.
     * Returns array of { userId, userName, conversations: [{ conversation_id, messages }] }
     */
    const groupBySalesmanAndConversation = (messages, currentUserId) => {
        // Primeiro, agrupar por conversation_id
        const convMap = {};
        (messages || []).forEach(msg => {
            const convId = msg.conversation_id;
            if (!convMap[convId]) {
                convMap[convId] = { conversation_id: convId, messages: [] };
            }
            convMap[convId].messages.push(msg);
        });

        // Para cada conversa, identificar o vendedor (usuário não-admin)
        const salesmenMap = {};
        Object.values(convMap).forEach(conv => {
            conv.messages.sort((a, b) => new Date(a.created) - new Date(b.created));

            // Encontrar o vendedor da conversa (primeiro remetente que não é admin)
            const salesmanMsg = conv.messages.find(m =>
                m.sender_role !== 'admin' && m.user_id_sender !== currentUserId
            ) || conv.messages[0];

            const salesmanId = salesmanMsg?.user_id_sender || 'unknown';
            const salesmanName = salesmanMsg?.sender_name || `Usuário #${salesmanId}`;

            if (!salesmenMap[salesmanId]) {
                salesmenMap[salesmanId] = {
                    userId: salesmanId,
                    userName: salesmanName,
                    conversations: []
                };
            }
            salesmenMap[salesmanId].conversations.push(conv);
        });

        // Ordenar conversas dentro de cada vendedor (mais recente primeiro)
        return Object.values(salesmenMap).map(s => {
            s.conversations.sort((a, b) => {
                const lastA = new Date(a.messages[a.messages.length - 1]?.created || 0);
                const lastB = new Date(b.messages[b.messages.length - 1]?.created || 0);
                return lastB - lastA;
            });
            s.totalMessages = s.conversations.reduce((sum, c) => sum + c.messages.length, 0);
            return s;
        });
    };

    if (loading) {
        return <div className="support-chat"><div className="support-loading">Carregando dados...</div></div>;
    }

    if (error) {
        return <div className="support-chat"><div className="support-error">{error}</div></div>;
    }

    if (!companies.length) {
        return <div className="support-chat"><div className="support-empty">Nenhuma empresa encontrada.</div></div>;
    }

    // Verificar se uma conversa está pendente (última mensagem de usuário não-admin)
    const isConversationPending = (conv) => {
        if (!conv.messages || conv.messages.length === 0) return false;
        const lastMsg = conv.messages[conv.messages.length - 1];
        return lastMsg.sender_role !== 'admin';
    };

    // Verificar se uma loja tem alguma conversa pendente
    const isStorePending = (salesmenGroups) => {
        return salesmenGroups.some(s => s.conversations.some(c => isConversationPending(c)));
    };

    // Verificar se uma empresa tem alguma loja pendente
    const isCompanyPending = (stores, subdomain) => {
        return stores.some(store => {
            const storeKey = `${subdomain}-${store.store_id}`;
            const messagesData = storeMessages[storeKey];
            if (!messagesData) return false;
            const groups = groupBySalesmanAndConversation(messagesData.messages, user?.user_id);
            return isStorePending(groups);
        });
    };

    return (
        <div className="support-chat">
            <h1>💬 Chat de Suporte</h1>

            {companies.map((company, companyIdx) => {
                const companyName = company.company_name || 'Empresa';
                const subdomain = company.subdomain || '';
                const stores = (company.units || []).flatMap(unit => unit.stores || []);
                const isCompanyOpen = expandedCompanies[companyIdx];
                const companyPending = isCompanyPending(stores, subdomain);

                return (
                    <div key={companyIdx} className="support-company">
                        <button
                            className="support-company-header"
                            onClick={() => toggleCompany(companyIdx)}
                        >
                            <span>🏢 {companyName} {companyPending && <span className="pending-badge">PENDENTE</span>}</span>
                            <span className={`toggle-icon ${isCompanyOpen ? 'open' : ''}`}>▶</span>
                        </button>

                        {isCompanyOpen && stores.map((store, storeIdx) => {
                            const storeKey = `${subdomain}-${store.store_id}`;
                            const isStoreOpen = expandedStores[storeKey];
                            const messagesData = storeMessages[storeKey];
                            const isLoadingMsgs = loadingMessages[storeKey];
                            const salesmenGroups = messagesData ? groupBySalesmanAndConversation(messagesData.messages, user?.user_id) : [];
                            const hasMessages = salesmenGroups.length > 0;
                            const storePending = hasMessages && isStorePending(salesmenGroups);

                            return (
                                <div key={storeIdx} className="support-store">
                                    <button
                                        className="support-store-header"
                                        onClick={() => toggleStore(storeKey, store, subdomain)}
                                    >
                                        <span>🏪 {store.store_display_name || store.store_name} {storePending && <span className="pending-badge">PENDENTE</span>}</span>
                                        <span className="store-header-right">
                                            {messagesData && hasMessages && (
                                                <span className="store-badge">{salesmenGroups.length} {salesmenGroups.length === 1 ? 'vendedor' : 'vendedores'}</span>
                                            )}
                                            <span className={`toggle-icon ${isStoreOpen ? 'open' : ''}`}>▶</span>
                                        </span>
                                    </button>

                                    {isStoreOpen && (
                                        <div className="support-sellers">
                                            {isLoadingMsgs && (
                                                <div className="support-messages-loading">Carregando mensagens...</div>
                                            )}

                                            {!isLoadingMsgs && !hasMessages && messagesData && (
                                                <div className="support-messages-loading">Nenhuma mensagem de suporte nesta loja.</div>
                                            )}

                                            {!isLoadingMsgs && salesmenGroups.map(salesman => {
                                                const salesmanKey = `${storeKey}-user-${salesman.userId}`;
                                                const isSalesmanOpen = expandedSalesmen[salesmanKey];
                                                const salesmanPending = salesman.conversations.some(c => isConversationPending(c));

                                                return (
                                                    <div key={salesman.userId} className="support-salesman">
                                                        <button
                                                            className="support-salesman-header"
                                                            onClick={() => toggleSalesman(salesmanKey)}
                                                        >
                                                            <span>👤 {salesman.userName} ({salesman.conversations.length} {salesman.conversations.length === 1 ? 'conversa' : 'conversas'}, {salesman.totalMessages} msg) {salesmanPending && <span className="pending-badge">PENDENTE</span>}</span>
                                                            <span className={`toggle-icon ${isSalesmanOpen ? 'open' : ''}`}>▶</span>
                                                        </button>

                                                        {isSalesmanOpen && salesman.conversations.map(conv => {
                                                            const convKey = `${storeKey}-${conv.conversation_id}`;
                                                            const isConvOpen = expandedConversations[convKey];
                                                            const replyText = replyTexts[convKey] || '';
                                                            const isSending = sendingReply[convKey];
                                                            // O dono da conversa é sempre conv.conversation_id (o backend grava
                                                            // conversation_id = user_id do vendedor dono). NÃO derivar de
                                                            // user_id_sender: um agente de suporte (role admin, ex. Julio) pode ter
                                                            // respondido e seria escolhido por engano, gerando user_id sem permissão
                                                            // na loja / com role inválida -> 400 no /support-message/send (visto como 500).
                                                            const conversationUserId = conv.conversation_id;
                                                            const convPending = isConversationPending(conv);
                                                            const isTargetConv = urlConversationId && String(conv.conversation_id) === String(urlConversationId);

                                                            return (
                                                                <div key={conv.conversation_id} ref={isTargetConv ? targetConvRef : null} className={`support-conversation${isTargetConv ? ' support-conversation-highlighted' : ''}`}>
                                                                    <div className="support-conversation-header-row">
                                                                        <button
                                                                            className="support-conversation-header"
                                                                            onClick={() => toggleConversation(convKey, conv, store.store_id)}
                                                                        >
                                                                            <span>💬 Conversa #{conv.conversation_id} ({conv.messages.length} msg) {convPending && <span className="pending-badge">PENDENTE</span>}</span>
                                                                            <span className={`toggle-icon ${isConvOpen ? 'open' : ''}`}>▶</span>
                                                                        </button>
                                                                        <button
                                                                            className="support-refresh-btn"
                                                                            onClick={(e) => { e.stopPropagation(); handleRefreshConversation(storeKey, store.store_id, subdomain); }}
                                                                            disabled={refreshingConversation[storeKey]}
                                                                            title="Atualizar conversa"
                                                                        >
                                                                            {refreshingConversation[storeKey] ? '⏳' : '🔄'}
                                                                        </button>
                                                                        <button
                                                                            className="support-clear-btn"
                                                                            onClick={(e) => { e.stopPropagation(); handleClearConversation(conv, store.store_id, storeKey); }}
                                                                            disabled={clearingConversation[convKey]}
                                                                            title="Limpar conversa"
                                                                        >
                                                                            {clearingConversation[convKey] ? '⏳' : '🗑️'}
                                                                        </button>
                                                                    </div>

                                                                    {isConvOpen && (
                                                                        <>
                                                                            <div className="support-messages">
                                                                                {conv.messages.map(msg => {
                                                                                    const isAdmin = msg.sender_role === 'admin';
                                                                                    return (
                                                                                        <div
                                                                                            key={msg.id}
                                                                                            className={`support-message ${isAdmin ? 'from-admin' : 'from-user'}`}
                                                                                        >
                                                                                            <div className="support-message-header">
                                                                                                <span className="support-message-sender">
                                                                                                    {msg.sender_role === 'admin'
                                                                                                        ? `Suporte Aliado (${msg.sender_name || `Usuário #${msg.user_id_sender}`})`
                                                                                                        : (msg.sender_name || `Usuário #${msg.user_id_sender}`)}
                                                                                                </span>
                                                                                                <span className="support-message-time">
                                                                                                    {formatTime(msg.created)}
                                                                                                </span>
                                                                                            </div>
                                                                                            {msg.message_type === 'image' && msg.metadata?.url ? (
                                                                                                <SupportImageAttachment msg={msg} bucketName={company.bucket_name} />
                                                                                            ) : msg.message_type === 'audio' && msg.metadata?.url ? (
                                                                                                <SupportAudioAttachment msg={msg} bucketName={company.bucket_name} />
                                                                                            ) : (
                                                                                                <div className="support-message-text">
                                                                                                    {msg.message}
                                                                                                </div>
                                                                                            )}
                                                                                            {isAdmin && (
                                                                                                <span
                                                                                                    className={`support-message-read-receipt ${msg.read_at ? 'read' : ''}`}
                                                                                                    title={msg.read_at ? `Visto em ${formatTime(msg.read_at)}` : 'Enviado, ainda não visto'}
                                                                                                >
                                                                                                    {msg.read_at ? '✓✓' : '✓'}
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                                <div ref={el => messagesEndRefs.current[convKey] = el} />
                                                                            </div>

                                                                            {(() => {
                                                                                const convBusy = attachmentBusy[convKey];
                                                                                const isRecording = convBusy === 'recording';
                                                                                const attachDisabled = isSending || (convBusy && !isRecording);
                                                                                return (
                                                                                    <>
                                                                                        <div className="support-attachment-actions">
                                                                                            <button
                                                                                                type="button"
                                                                                                className="support-attach-btn"
                                                                                                title="Anexar imagem (JPEG/PNG)"
                                                                                                onClick={() => handleAttachImageClick(convKey, conv, store, company, subdomain, storeKey, conversationUserId)}
                                                                                                disabled={attachDisabled}
                                                                                            >
                                                                                                {convBusy === 'uploading-image' ? '⏳ Enviando imagem...' : '📎 Imagem'}
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                className={`support-record-btn ${isRecording ? 'recording' : ''}`}
                                                                                                title={isRecording ? 'Parar e enviar gravação' : 'Gravar áudio'}
                                                                                                onClick={() => isRecording
                                                                                                    ? handleStopRecording(convKey, conv, store, company, subdomain, storeKey, conversationUserId)
                                                                                                    : handleStartRecording(convKey)}
                                                                                                disabled={isSending || (convBusy && !isRecording)}
                                                                                            >
                                                                                                {isRecording
                                                                                                    ? `⏹ Parar (${recordingElapsed[convKey] || 0}s)`
                                                                                                    : convBusy === 'converting'
                                                                                                        ? '⏳ Convertendo áudio...'
                                                                                                        : convBusy === 'uploading-audio'
                                                                                                            ? '⏳ Enviando áudio...'
                                                                                                            : '🎙️ Gravar áudio'}
                                                                                            </button>
                                                                                        </div>
                                                                                        {attachmentError[convKey] && (
                                                                                            <div className="support-attachment-send-error">{attachmentError[convKey]}</div>
                                                                                        )}
                                                                                    </>
                                                                                );
                                                                            })()}
                                                                            <div className="support-reply-form">
                                                                                <textarea
                                                                                    rows="3"
                                                                                    className="support-reply-input"
                                                                                    placeholder="Digite sua resposta..."
                                                                                    value={replyText}
                                                                                    onChange={(e) => handleReplyChange(convKey, e.target.value)}
                                                                                    onKeyDown={(e) => handleKeyDown(e, conv.conversation_id, store.store_id, subdomain, storeKey, conversationUserId)}
                                                                                    disabled={isSending}
                                                                                />
                                                                                <button
                                                                                    className="support-reply-btn"
                                                                                    onClick={() => handleSendReply(conv.conversation_id, store.store_id, subdomain, storeKey, conversationUserId)}
                                                                                    disabled={isSending || !replyText.trim()}
                                                                                >
                                                                                    {isSending ? 'Enviando...' : 'Enviar'}
                                                                                </button>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}

            <input
                type="file"
                accept="image/jpeg,image/png"
                ref={imageInputRef}
                onChange={handleImageFileSelected}
                style={{ display: 'none' }}
            />

            {confirmModal && (
                <div className="confirm-modal-overlay" onClick={() => setConfirmModal(null)}>
                    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="confirm-modal-icon">⚠️</div>
                        <h3 className="confirm-modal-title">Limpar conversa</h3>
                        <p className="confirm-modal-text">
                            Tem certeza que deseja apagar todas as <strong>{confirmModal.messageCount}</strong> mensagens desta conversa? Esta ação não pode ser desfeita.
                        </p>
                        <div className="confirm-modal-actions">
                            <button className="confirm-modal-cancel" onClick={() => setConfirmModal(null)}>
                                Cancelar
                            </button>
                            <button className="confirm-modal-confirm" onClick={confirmModal.onConfirm}>
                                Apagar mensagens
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupportChat;
